import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import { useEditEmployeeMutation } from '../../redux/apis/employee';

import { getErrorMessage, withoutProfilePicture } from '../../utils/handler';
import { notification } from '../../utils/notification';
import { adminProfile } from '../../utils/schemas/auth';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';
import { INPUT_LABEL, PLACEHOLDER } from '../../utils/constant/placeHolders';
import { ROLES } from '../../utils/constant/global';
import EmployeeProfile from '../Employee/EmployeeProfile';
import { useDisconnectGoogleCalendarMutation } from '../../redux/apis/googleCalendarApi';
import { Spinner } from 'react-bootstrap';

import './AdminProfile.scss';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;
const { LB_EMAIL, LB_MOBILE, LB_NAME } = INPUT_LABEL;
const { PH_NAME, PH_EMAIL, PH_MOBILE } = PLACEHOLDER;

export default function AdminProfile() {
  const { data, refetch } = useIsLoggedInQuery();
  const [editEmployee] = useEditEmployeeMutation();

  const [disconnectGoogleCalendar, { isLoading }] = useDisconnectGoogleCalendarMutation();

  const handleDisconnectGoogleCalendar = async () => {
    try {
      const res = await disconnectGoogleCalendar();

      if (res.data.success) {
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
        refetch();
      }
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(adminProfile),
    values: {
      ...data?.data
    }
  });

  const onSubmit = async (editData) => {
    try {
      const { name, email, phone } = editData;
      const employeeData = {
        email: email,
        name: name,
        phone: String(phone)
      };
      const res = await editEmployee({ data: employeeData, id: data?.data?._id });
      if (res.data?.success) {
        refetch();
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
      } else {
        notification(res?.data?.statusMessage, TOAST_ERROR);
      }
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  return (
    <>
      {data.data.hrxRole.roleName !== ROLES.ADMIN ? (
        <EmployeeProfile />
      ) : (
        <div className="admin-profile container-xxl">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="admin-profile__card">
              <div className="admin-profile__hero">
                <span className="admin-profile__hero-bg" aria-hidden />
                <div className="admin-profile__hero-main">
                  <span className="admin-profile__avatar-ring">
                    <img
                      className="admin-profile__avatar"
                      src={
                        data?.data?.profilePicture
                          ? data?.data?.profilePicture
                          : withoutProfilePicture(data.data.name)
                      }
                      alt="profile"
                    />
                  </span>
                  <div className="admin-profile__identity">
                    <h2 className="admin-profile__name">{data.data.name}</h2>
                    <p className="admin-profile__email">{data?.data?.email}</p>
                    {data?.data?.hrxRole?.roleName ? (
                      <span className="admin-profile__role-badge">
                        <i className="icofont-crown" aria-hidden />
                        {data.data.hrxRole.roleName}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="admin-profile__body">
                <div className="admin-profile__section-head">
                  <span className="admin-profile__section-title">Personal information</span>
                </div>

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="admin-profile__field">
                      <label htmlFor="exampleFormControlInput877" className="admin-profile__label">
                        {LB_NAME}
                      </label>
                      <div className="admin-profile__input-wrap">
                        <i className="icofont-user-alt-3 admin-profile__input-icon" aria-hidden />
                        <input
                          type="text"
                          {...register('name')}
                          className="form-control admin-profile__input"
                          id="exampleFormControlInput877"
                          placeholder={PH_NAME}
                          disabled
                        />
                      </div>
                      <small className="text-danger">{getErrorMessage('name', errors)}</small>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="admin-profile__field">
                      <label htmlFor="exampleFormControlInput1778" className="admin-profile__label">
                        {LB_EMAIL}
                      </label>
                      <div className="admin-profile__input-wrap">
                        <i className="icofont-email admin-profile__input-icon" aria-hidden />
                        <input
                          type="text"
                          {...register('email')}
                          className="form-control admin-profile__input"
                          id="exampleFormControlInput1778"
                          placeholder={PH_EMAIL}
                          disabled
                        />
                      </div>
                      <small className="text-danger">{getErrorMessage('email', errors)}</small>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="admin-profile__field">
                      <label
                        htmlFor="exampleFormControlInputMobile"
                        className="admin-profile__label"
                      >
                        {LB_MOBILE}
                      </label>
                      <div className="admin-profile__input-wrap">
                        <i className="icofont-smart-phone admin-profile__input-icon" aria-hidden />
                        <input
                          type="number"
                          {...register('phone')}
                          className="form-control admin-profile__input"
                          id="exampleFormControlInputMobile"
                          placeholder={PH_MOBILE}
                          disabled
                        />
                      </div>
                      <small className="text-danger">{getErrorMessage('phone', errors)}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>

          {/* Google Calendar Disconnect Button  */}
          {data?.data?.isCalendarConnected && (
            <div className="admin-profile__integration">
              <div className="admin-profile__integration-left">
                <span className="admin-profile__integration-icon">
                  <i className="icofont-ui-calendar" aria-hidden />
                </span>
                <div className="admin-profile__integration-meta">
                  <span className="admin-profile__integration-name">Google Calendar</span>
                  <span className="admin-profile__integration-status">
                    <span className="admin-profile__integration-dot" aria-hidden />
                    Connected
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleDisconnectGoogleCalendar}
                className="admin-profile__disconnect-btn"
              >
                {isLoading ? <Spinner size="sm" /> : 'Disconnect'}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
